import { useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useApp, useFetch } from '../store/app';
import { Fade } from './common';
import { Icon } from '../components/Icon';
import { Card, Button, Avatar, StatusBadge, Tag } from '../components/primitives';
import { AuditEditor, AuditorEditor, AuditInvoiceEditor } from './preaEditors';
import { st } from '../lib/status';
import { av } from '../lib/avatar';
import { money } from '../lib/format';
import { api } from '../lib/api';
import { can } from '../lib/rbac';
import type { AuditDetail, AuditInvoice, Project, ProjectStage, ProjectTeamMember } from '../../shared/types';

interface ProjectFull extends Project {
  contract_name?: string | null;
  qbo_configured?: boolean;
  audit: AuditDetail | null;
  stages: ProjectStage[];
  team: ProjectTeamMember[];
  invoices: AuditInvoice[];
}

// Invoice status → correct label + a color tone borrowed from st(). st() returns
// both color AND label, so we keep its colors but override the label to the
// real invoice status (otherwise 'ready'→"Planning", 'sent'→"Approved").
const INVOICE_BADGE: Record<string, { label: string; tone: string }> = {
  draft: { label: 'Draft', tone: 'draft' },
  ready: { label: 'Ready', tone: 'planning' },
  sent: { label: 'Sent', tone: 'approved' },
  paid: { label: 'Paid', tone: 'paid' },
};
const invoiceBadge = (status: string) => {
  const b = INVOICE_BADGE[status] || INVOICE_BADGE.draft;
  return { ...st(b.tone), label: b.label };
};

export function ProjectDetail() {
  const { id: idParam } = useParams();
  const id = Number(idParam);
  const nav = useNavigate();
  const { openDrawer, refresh, user } = useApp();
  const { data: p, error } = useFetch<ProjectFull>(`/projects/${id}`);
  const [editAudit, setEditAudit] = useState(false);
  const [editAuditor, setEditAuditor] = useState<ProjectTeamMember | 'new' | null>(null);
  const [editInvoice, setEditInvoice] = useState<AuditInvoice | 'new' | null>(null);
  const [syncNote, setSyncNote] = useState('');

  const canEdit = !!user && can(user.role, 'update', 'projects');

  if (error) return <BackShell nav={nav}><Empty>Couldn’t load this project.</Empty></BackShell>;
  if (!p) return <BackShell nav={nav}><Empty>Loading…</Empty></BackShell>;

  const audit = p.audit;
  const feeTotal = p.team.reduce((s, m) => s + (m.fee || 0), 0);
  const bid = audit?.bid_amount || 0;
  const profit = bid - feeTotal;
  const dates = [audit?.audit_start, audit?.audit_end].filter(Boolean).join(' – ') || p.due_date || '—';

  const toggleStage = async (s: ProjectStage) => {
    if (!canEdit) return;
    await api.put(`/projects/${id}/stages/${s.id}`, { done: !s.done });
    refresh();
  };

  const syncInvoice = async (inv: AuditInvoice) => {
    setSyncNote('');
    const res = await api.post<{ synced: boolean; reason?: string }>(`/projects/${id}/invoices/${inv.id}/sync`, {});
    setSyncNote(res.synced ? 'Synced to QuickBooks.' : 'QuickBooks isn’t connected yet — marked pending. Add the QBO # manually for now.');
    refresh();
  };

  return (
    <Fade>
      <BackLink nav={nav} />
      <Card style={{ overflow: 'hidden', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '18px 20px', borderBottom: '1px solid var(--border)' }}>
          <div style={{ width: 52, height: 52, flex: '0 0 auto', borderRadius: 12, background: 'var(--accent-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon name="verified_user" size={26} color="var(--accent)" /></div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 20, fontWeight: 700, letterSpacing: '-.01em' }}>{p.name}</span>
              <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.02em', color: 'var(--accent)', background: 'var(--accent-bg)', padding: '2px 7px', borderRadius: 4 }}>{p.type}</span>
              <StatusBadge badge={st(p.status)} />
            </div>
            <div style={{ fontSize: 12.5, color: 'var(--text-3)', marginTop: 2 }}>{[p.client_name, p.contract_name].filter((x) => x && x !== '—').join(' · ') || '—'}</div>
          </div>
          {canEdit && <Button variant="secondary" icon="edit" onClick={() => openDrawer({ type: 'project', mode: 'edit', id })}>Edit</Button>}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, padding: '16px 20px' }}>
          {metaCell('Facility', audit?.facility_label || '—', audit?.facility_type)}
          {metaCell('State', audit?.facility_state || '—')}
          {metaCell('Fieldwork', dates)}
          {metaCell('Report', audit?.report_type || '—', audit?.month_bucket)}
        </div>
      </Card>

      {/* Financials */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 18 }}>
        {stat('Bid', money(bid), 'var(--text)')}
        {stat('Auditor fees', money(feeTotal), 'var(--text-2)')}
        {stat('Profit', money(profit), profit < 0 ? 'var(--danger)' : 'var(--success)')}
      </div>

      <Section title="Audit details" action={canEdit ? <Button variant="ghost" icon="edit" onClick={() => setEditAudit(true)}>Edit details</Button> : null}>
        {!audit && <Empty>No audit details yet — click “Edit details”.</Empty>}
        {audit && (
          <Row>
            <Icon name="location_on" size={20} color="var(--text-3)" />
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 13.5 }}>{audit.facility_label || 'No facility set'}</div>
              <div style={{ fontSize: 11.5, color: 'var(--text-3)' }}>{[audit.facility_type, audit.facility_state, dates].filter(Boolean).join(' · ') || '—'}</div>
            </div>
          </Row>
        )}
      </Section>

      <Section title="Pipeline">
        {p.stages.length === 0 && <Empty>No pipeline stages.</Empty>}
        {p.stages.map((s) => (
          <div key={s.id} onClick={() => toggleStage(s)} className={canEdit ? 'cc-hover-border cc-clickable' : undefined} style={{ display: 'flex', alignItems: 'center', gap: 11, border: '1px solid var(--border)', borderRadius: 10, padding: '10px 12px', background: 'var(--surface)' }}>
            <Icon name={s.done ? 'check_circle' : 'radio_button_unchecked'} size={20} fill={s.done ? 1 : 0} color={s.done ? 'var(--success)' : 'var(--text-3)'} />
            <span style={{ flex: 1, fontSize: 13.5, fontWeight: 600, color: s.done ? 'var(--text-2)' : 'var(--text)' }}>{s.label}</span>
            {s.done && s.done_date && <span style={{ fontSize: 11.5, color: 'var(--text-3)' }}>{s.done_date}</span>}
          </div>
        ))}
      </Section>

      <Section title={`Auditors (${p.team.length})`} action={canEdit ? <Button variant="ghost" icon="add" onClick={() => setEditAuditor('new')}>Add auditor</Button> : null}>
        {p.team.length === 0 && <Empty>No auditors assigned.</Empty>}
        {p.team.map((m) => {
          const a = av(m.name, m.avatar_kind);
          return (
            <Row key={m.assignment_id} onClick={canEdit ? () => setEditAuditor(m) : undefined} canEdit={canEdit}>
              <Avatar initials={a.in} bg={a.bg} fg={a.fg} size={34} />
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 13.5 }}>{m.name}</div>
                <div style={{ fontSize: 11.5, color: 'var(--text-3)' }}>{m.role || 'Auditor'}</div>
              </div>
              <span className="cc-mono" style={{ fontSize: 13, fontWeight: 600 }}>{money(m.fee)}</span>
              {canEdit && <Icon name="chevron_right" size={18} color="var(--text-3)" />}
            </Row>
          );
        })}
      </Section>

      <Section title={`Invoicing (${p.invoices.length})`} action={canEdit ? <Button variant="ghost" icon="add" onClick={() => setEditInvoice('new')}>Generate invoice</Button> : null}>
        {p.invoices.length === 0 && <Empty>No invoices yet. “Generate invoice” bills the agency the bid amount.</Empty>}
        {p.invoices.map((inv) => (
          <Row key={inv.id} onClick={canEdit ? () => setEditInvoice(inv) : undefined} canEdit={canEdit}>
            <Icon name="request_quote" size={20} color="var(--text-3)" />
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span className="cc-mono" style={{ fontWeight: 600, fontSize: 13.5 }}>{money(inv.amount)}</span>
                <StatusBadge badge={invoiceBadge(inv.status)} />
                {inv.external_ref && <span className="cc-mono" style={{ fontSize: 11.5, color: 'var(--text-3)' }}>{inv.external_ref}</span>}
              </div>
              <div style={{ fontSize: 11, color: inv.qbo_sync_state === 'synced' ? 'var(--success)' : 'var(--text-3)' }}>
                QuickBooks: {inv.qbo_sync_state === 'synced' ? `synced ${inv.qbo_synced_at}` : inv.qbo_sync_state === 'pending' ? 'pending (not connected)' : 'not synced'}
              </div>
            </div>
            {canEdit && <Button variant="secondary" icon="sync" onClick={(e) => { e.stopPropagation(); syncInvoice(inv); }} style={{ padding: '6px 10px' }}>Sync</Button>}
          </Row>
        ))}
        {syncNote && <div style={{ fontSize: 12, color: 'var(--text-3)', padding: '2px 2px' }}>{syncNote}</div>}
        {p.qbo_configured === false && p.invoices.length > 0 && (
          <div style={{ fontSize: 11.5, color: 'var(--text-3)', padding: '2px 2px' }}>QuickBooks integration isn’t connected yet — invoices track here and can be synced once keys are added.</div>
        )}
      </Section>

      {p.tags.length > 0 && (
        <Section title="Tags">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>{p.tags.map((t) => <Tag key={t}>{t}</Tag>)}</div>
        </Section>
      )}

      {editAudit && <AuditEditor projectId={id} clientId={p.client_id} audit={audit} onClose={() => setEditAudit(false)} onSaved={() => { refresh(); setEditAudit(false); }} />}
      {editAuditor && <AuditorEditor projectId={id} member={editAuditor === 'new' ? null : editAuditor} onClose={() => setEditAuditor(null)} onSaved={() => { refresh(); setEditAuditor(null); }} />}
      {editInvoice && <AuditInvoiceEditor projectId={id} invoice={editInvoice === 'new' ? null : editInvoice} defaultAmount={bid} onClose={() => setEditInvoice(null)} onSaved={() => { refresh(); setEditInvoice(null); }} />}
    </Fade>
  );
}

function BackLink({ nav }: { nav: ReturnType<typeof useNavigate> }) {
  return (
    <button onClick={() => nav('/projects')} className="cc-hover-s2 cc-clickable" style={{ appearance: 'none', border: 0, background: 'transparent', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 13, fontWeight: 600, color: 'var(--text-3)', padding: '4px 8px 4px 4px', borderRadius: 7, marginBottom: 12 }}>
      <Icon name="arrow_back" size={18} />Projects
    </button>
  );
}

function BackShell({ nav, children }: { nav: ReturnType<typeof useNavigate>; children: ReactNode }) {
  return <Fade><BackLink nav={nav} />{children}</Fade>;
}

function Section({ title, action, children }: { title: string; action?: ReactNode; children: ReactNode }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ fontWeight: 600, fontSize: 14 }}>{title}</div>
        <div style={{ flex: 1 }} />
        {action}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>{children}</div>
    </div>
  );
}

function Row({ children, onClick, canEdit }: { children: ReactNode; onClick?: () => void; canEdit?: boolean }) {
  return (
    <div onClick={onClick} className={canEdit && onClick ? 'cc-hover-border cc-clickable' : undefined} style={{ display: 'flex', alignItems: 'center', gap: 11, border: '1px solid var(--border)', borderRadius: 10, padding: 12, background: 'var(--surface)' }}>
      {children}
    </div>
  );
}

function stat(label: string, value: string, color: string) {
  return (
    <Card style={{ padding: '14px 16px', borderRadius: 11 }}>
      <div style={{ fontSize: 12.5, color: 'var(--text-2)', fontWeight: 500 }}>{label}</div>
      <div className="cc-mono" style={{ fontSize: 22, fontWeight: 700, letterSpacing: '-.02em', marginTop: 4, color }}>{value}</div>
    </Card>
  );
}

function Empty({ children }: { children: ReactNode }) {
  return <div style={{ fontSize: 12.5, color: 'var(--text-3)', padding: '4px 2px' }}>{children}</div>;
}

function metaCell(label: string, value: ReactNode, sub?: string) {
  return (
    <div key={label}>
      <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: 500 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{sub}</div>}
    </div>
  );
}
